import Service from 'react-reservice';

import { EEventMessageType } from '@webtrader/core/chart/events';
import { EChartProvider } from '@webtrader/core/chart/provider';
import { isEmpty } from '@webtrader/core/form/validator';
import { chartService } from '@webtrader/core/services/chart';
import { companyService } from '@webtrader/core/services/company';
import { TENANT_THEMES_MAP } from '@webtrader/core/services/company/constants';
import { configService } from '@webtrader/core/services/config';
import { EAppType } from '@webtrader/core/services/config/service';
import { userPreferenceService } from '@webtrader/core/services/userPreference';
import storage, { EStorageKey } from '@webtrader/core/storage';
import { EAppTheme, EThemeScheme, EThemeTemplate, IAppTheme, IFonts, hexToRgb } from '@webtrader/core/themes';
import { DARK_THEME_COLORS, IColors, LIGHT_THEME_COLORS } from '@webtrader/core/themes/colors';
import { DEFAULT_FONTS, POPPINS_FONTS } from '@webtrader/core/themes/fonts';
import { HANTEC_DARK_THEME_COLORS, HANTEC_LIGHT_THEME_COLORS } from '@webtrader/core/themes/hantec';
import { BASE_SIZES } from '@webtrader/core/themes/sizes';
import { isSystemDarkTheme } from '@webtrader/core/validators/device';

interface IAppThemeContext {
  ready: boolean;
  theme: IAppTheme;
}

interface ITenantTheme {
  darkThemeColors: IColors;
  lightThemeColors: IColors;
  font: IFonts;
  defaultTheme: EThemeScheme | undefined;
}

const defaultContext: IAppThemeContext = {
  ready: false,
  theme: {
    sizes: BASE_SIZES,
    colors: LIGHT_THEME_COLORS,
    scheme: EThemeScheme.LIGHT,
    font: DEFAULT_FONTS
  }
};

class ThemeService extends Service<IAppThemeContext> {
  private systemColors: IColors = defaultContext.theme.colors;

  constructor() {
    super();

    this.context = {
      ...defaultContext
    };
  }

  // This function is used to set the theme by tenantId once we get the tenantId from the company service - WT-8606
  // Do not use this function in backoffice
  public setThemeByTenant = (_tenantId?: string, backoffice?: boolean) => {
    if (backoffice) {
      return;
    }

    const { darkThemeColors, lightThemeColors, font, defaultTheme } = this.getThemeByTenant(_tenantId);

    const themeScheme = storage.getItem<EThemeScheme>(EStorageKey.THEME_SCHEME) ?? defaultTheme;
    switch (themeScheme) {
      case EThemeScheme.DARK:
        this.setTheme({
          ...this.context.theme,
          colors: darkThemeColors,
          scheme: EThemeScheme.DARK,
          font
        });
        break;
      case EThemeScheme.LIGHT:
        this.setTheme({
          ...this.context.theme,
          colors: lightThemeColors,
          scheme: EThemeScheme.LIGHT,
          font
        });
        break;
      default:
        this.setTheme({
          ...this.context.theme,
          colors: isSystemDarkTheme() ? darkThemeColors : lightThemeColors,
          scheme: EThemeScheme.SYSTEM,
          font
        });
        break;
    }
  };

  // By this time , the tenant Id is not ready yet , so we will only use the default theme colors
  public initPc = (useSystemScheme = true) => {
    configService.setAppType(EAppType.PC);
    const themeScheme = storage.getItem<EThemeScheme>(EStorageKey.THEME_SCHEME);

    if (isEmpty(themeScheme)) {
      if (useSystemScheme) {
        this.setTheme(
          {
            ...this.context.theme,
            colors: isSystemDarkTheme() ? DARK_THEME_COLORS : LIGHT_THEME_COLORS,
            scheme: EThemeScheme.SYSTEM
          },
          false
        );
      }
    } else {
      switch (themeScheme) {
        case EThemeScheme.DARK:
          this.setTheme(
            {
              ...this.context.theme,
              colors: DARK_THEME_COLORS,
              scheme: EThemeScheme.DARK
            },
            false
          );
          break;
        case EThemeScheme.LIGHT:
          this.setTheme(
            {
              ...this.context.theme,
              colors: LIGHT_THEME_COLORS,
              scheme: EThemeScheme.LIGHT
            },
            false
          );
          break;
        default:
          this.setTheme(
            {
              ...this.context.theme,
              colors: isSystemDarkTheme() ? DARK_THEME_COLORS : LIGHT_THEME_COLORS,
              scheme: EThemeScheme.SYSTEM
            },
            false
          );
          break;
      }
    }

    this.context.ready = true;
  };

  public initMobile = (systemThemeColors: IColors) => {
    configService.setAppType(EAppType.MOBILE);
    this.systemColors = systemThemeColors;

    const themeScheme = storage.getItem<EThemeScheme>(EStorageKey.THEME_SCHEME);

    switch (themeScheme) {
      case EThemeScheme.DARK:
        this.setTheme(
          {
            ...this.context.theme,
            colors: DARK_THEME_COLORS,
            scheme: themeScheme
          },
          false
        );
        break;
      case EThemeScheme.LIGHT:
        this.setTheme(
          {
            ...this.context.theme,
            colors: LIGHT_THEME_COLORS,
            scheme: themeScheme
          },
          false
        );
        break;
      default:
        this.setTheme(
          {
            ...this.context.theme,
            colors: systemThemeColors,
            scheme: EThemeScheme.SYSTEM
          },
          false
        );
        break;
    }

    this.context.ready = true;
  };

  public initBackoffice = () => {
    configService.setAppType(EAppType.PC);
    this.context.ready = true;
  };

  public setSystemColors = (systemColors: IColors) => {
    this.systemColors = systemColors;
  };

  public checkAndSwitchSystemTheme = () => {
    const isUsingSystemTheme = this.context.theme.scheme === EThemeScheme.SYSTEM;

    if (isUsingSystemTheme) {
      const colors = this.schemeToColors(EThemeScheme.SYSTEM);

      this.setTheme({
        ...this.context.theme,
        colors
      });
    }
  };

  public setPrimaryColor = (primaryColor?: string) => {
    if (primaryColor && primaryColor !== '') {
      this.context.theme = {
        ...this.context.theme,
        colors: {
          ...this.context.theme.colors,
          primary: hexToRgb(primaryColor) || primaryColor
        }
      };
    }
  };

  public setDefaultCandlestickColors = (candlestickColors: IColors['candlestick']) => {
    const candlestick = {
      positive: hexToRgb(candlestickColors.positive) || candlestickColors.positive,
      negative: hexToRgb(candlestickColors.negative) || candlestickColors.negative
    };

    this.context.theme = {
      ...this.context.theme,
      colors: {
        ...this.context.theme.colors,
        candlestick
      }
    };
  };

  public switchThemeScheme = (scheme: EThemeScheme, switchScheme = true) => {
    const nextColors = this.schemeToColors(scheme);

    this.setTheme({
      ...this.context.theme,
      colors: nextColors,
      scheme: switchScheme ? scheme : this.context.theme.scheme
    });

    const returnValue = {
      ...nextColors,
      primary: this.context.theme.colors.primary
    };

    if (configService.getAppType() === EAppType.PC) {
      const provider = chartService.context.selectedProvider;

      if (provider === EChartProvider.ORIGINAL) {
        chartService.postEventMessage({
          type: EEventMessageType.THEME_SCHEME,
          data: returnValue
        });
      } else {
        chartService.setTVTheme(this.isDarkTheme() ? EAppTheme.DARK : EAppTheme.LIGHT);
      }
    }

    return returnValue;
  };

  public isDarkTheme = () => {
    if (this.context.theme.scheme === EThemeScheme.SYSTEM) {
      return isSystemDarkTheme();
    }

    return this.context.theme.scheme === EThemeScheme.DARK;
  };

  public resetWhenRootUnmounted = () => {
    this.context.ready = false;
  };

  private setTheme = (theme: IAppTheme, shouldStoreTheme = true) => {
    shouldStoreTheme && storage.setItem(EStorageKey.THEME_SCHEME, theme.scheme);

    this.context.theme = {
      ...theme,
      colors: {
        ...theme.colors,
        primary: this.context.theme.colors.primary,
        candlestick: this.context.theme.colors.candlestick
      }
    };
  };

  private schemeToColors = (scheme: EThemeScheme) => {
    let themeColors;
    const { darkThemeColors, lightThemeColors } = this.getThemeByTenant();
    const candlestickColorUserPreference = userPreferenceService.context.candlestickColorUserPreference;

    switch (scheme) {
      case EThemeScheme.DARK:
        themeColors = darkThemeColors;
        break;
      case EThemeScheme.LIGHT:
        themeColors = lightThemeColors;
        break;
      default:
        if (configService.getAppType() === EAppType.PC) {
          themeColors =
            window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
              ? darkThemeColors
              : lightThemeColors;
        } else {
          themeColors = this.systemColors;
        }
        break;
    }

    return candlestickColorUserPreference
      ? { ...themeColors, candlestick: candlestickColorUserPreference }
      : themeColors;
  };

  private getThemeByTenant = (_tenantId?: string): ITenantTheme => {
    const tenantId = _tenantId || companyService.context.company?.tenantId;
    const themeTemplate = tenantId ? TENANT_THEMES_MAP[tenantId] : undefined;

    if (themeTemplate === EThemeTemplate.HANTEC) {
      return {
        darkThemeColors: HANTEC_DARK_THEME_COLORS,
        lightThemeColors: HANTEC_LIGHT_THEME_COLORS,
        font: POPPINS_FONTS,
        defaultTheme: EThemeScheme.DARK
      };
    }

    return {
      darkThemeColors: DARK_THEME_COLORS,
      lightThemeColors: LIGHT_THEME_COLORS,
      font: DEFAULT_FONTS,
      defaultTheme: undefined
    };
  };
}

export default new ThemeService();
